import React, { useState, useEffect, useContext } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { Plus, Edit3, ChevronDown, X, Code } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { usePolicies, usePolicyMutations, usePolicyDetail } from '../../../hooks/console/useConsoleAPI';
import Pagination from '../shared/Pagination';
import { AuthContext } from '../../../../../../context/AuthContext';
import { toast } from 'react-toastify';

const PER = 5;
const HISTORY_PER = 5;

const POLICY_TYPES = {
    SEARCH_RULE:   { label: '검색 규칙',     color: 'text-blue-500',    bg: 'bg-blue-100' },
    ANSWER_FORMAT: { label: '답변 구성 규칙', color: 'text-purple-500',  bg: 'bg-purple-100' },
    FORBIDDEN:     { label: '금지 규칙',     color: 'text-red-500',     bg: 'bg-red-100' },
    INTERPRETATION:{ label: '질문 해석 규칙', color: 'text-amber-500',   bg: 'bg-amber-100' },
    RERANK:        { label: '검색 결과 순위 조정', color: 'text-emerald-500', bg: 'bg-emerald-100' },
};

const POLICY_TYPE_DESC = {
    SEARCH_RULE:    '관련 법령·조문을 더 잘 찾아오도록 검색 방식을 조정합니다.',
    ANSWER_FORMAT:  'AI 답변의 구성·문체·필수 포함 항목을 지정합니다.',
    FORBIDDEN:      '특정 표현이나 잘못된 안내가 답변에 나오지 않도록 차단합니다.',
    INTERPRETATION: '사용자 질문의 의도를 더 정확히 해석하도록 규칙을 추가합니다.',
    RERANK:         '검색된 결과의 우선순위를 다시 정렬해 더 정확한 근거를 사용합니다.',
};

const EMPTY_FORM = {
    policy_code: '', policy_type: 'SEARCH_RULE',
    title: '', description: '',
    target_scope: 'GLOBAL', target_value: '',
    tags_json: '',
    is_active: 'Y', priority_no: 10,
};

const EMPTY_RULE_FIELDS = {
    terms_any: [],
    targets_any: [],
    must_search_articles: [],
    banned_phrases: [],
    must_include_sections: [],
};

// rule_payload_json 객체 → ruleFields 로 파싱
const parsePayloadToFields = (payload) => {
    try {
        const obj = typeof payload === 'string' ? JSON.parse(payload) : (payload || {});
        return {
            terms_any:             obj.conditions?.terms_any             || [],
            targets_any:           obj.conditions?.targets_any           || [],
            must_search_articles:  obj.actions?.must_search_articles     || obj.actions?.boost_articles || [],
            banned_phrases:        obj.actions?.banned_phrases           || [],
            must_include_sections: obj.actions?.must_include_sections    || [],
        };
    } catch {
        return { ...EMPTY_RULE_FIELDS };
    }
};

// ruleFields → rule_payload_json 객체 조립
const buildPayload = (type, fields) => {
    const conditions = {};
    if (fields.targets_any.length) conditions.targets_any = fields.targets_any;
    if (fields.terms_any.length)   conditions.terms_any   = fields.terms_any;

    switch (type) {
        case 'SEARCH_RULE':
            return {
                rule_kind: 'search_boost',
                ...(Object.keys(conditions).length ? { conditions } : {}),
                actions: { must_search_articles: fields.must_search_articles },
            };
        case 'FORBIDDEN':
            return {
                rule_kind: 'forbidden_phrase',
                actions: { banned_phrases: fields.banned_phrases },
            };
        case 'ANSWER_FORMAT':
            return {
                rule_kind: 'answer_format',
                ...(Object.keys(conditions).length ? { conditions } : {}),
                actions: { must_include_sections: fields.must_include_sections },
            };
        case 'INTERPRETATION':
            return {
                rule_kind: 'interpretation',
                ...(Object.keys(conditions).length ? { conditions } : {}),
                actions: {},
            };
        case 'RERANK':
            return {
                rule_kind: 'rerank',
                ...(Object.keys(conditions).length ? { conditions } : {}),
                actions: { boost_articles: fields.must_search_articles },
            };
        default:
            return {};
    }
};

// 태그 입력 컴포넌트
const TagInput = ({ label, values, onChange, placeholder, color = 'blue' }) => {
    const [input, setInput] = useState('');

    const colorMap = {
        blue:   { chip: 'bg-blue-100 text-blue-700',   btn: 'bg-blue-500 hover:bg-blue-600' },
        red:    { chip: 'bg-red-100 text-red-700',     btn: 'bg-red-500 hover:bg-red-600' },
        purple: { chip: 'bg-purple-100 text-purple-700', btn: 'bg-purple-500 hover:bg-purple-600' },
        amber:  { chip: 'bg-amber-100 text-amber-700', btn: 'bg-amber-500 hover:bg-amber-600' },
        emerald:{ chip: 'bg-emerald-100 text-emerald-700', btn: 'bg-emerald-500 hover:bg-emerald-600' },
    };
    const c = colorMap[color] || colorMap.blue;

    const add = () => {
        const v = input.trim();
        if (v && !values.includes(v)) onChange([...values, v]);
        setInput('');
    };

    return (
        <div>
            <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">{label}</label>
            <div className="flex flex-wrap gap-1 min-h-[24px] mb-1.5">
                {values.map((v, i) => (
                    <span key={i} className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-mono ${c.chip}`}>
                        {v}
                        <button
                            type="button"
                            onClick={() => onChange(values.filter((_, j) => j !== i))}
                            className="opacity-60 hover:opacity-100 leading-none"
                        >
                            <X size={10} />
                        </button>
                    </span>
                ))}
                {values.length === 0 && <span className="text-xs text-gray-300 italic">없음</span>}
            </div>
            <div className="flex gap-1">
                <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                    placeholder={placeholder}
                    className="flex-1 px-3 py-1.5 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary font-mono"
                />
                <button
                    type="button"
                    onClick={add}
                    className={`px-3 py-1.5 rounded-lg text-base text-white transition-colors ${c.btn}`}
                >
                    + 추가
                </button>
            </div>
        </div>
    );
};

// policy_type별 동적 폼 필드
const RuleFieldsForm = ({ policyType, ruleFields, onChange }) => {
    const set = (key) => (val) => onChange({ ...ruleFields, [key]: val });

    switch (policyType) {
        case 'SEARCH_RULE':
            return (
                <div className="space-y-3 p-4 rounded-xl bg-blue-50 border border-blue-100">
                    <div>
                        <p className="text-sm font-semibold text-blue-600 uppercase tracking-wider">검색 규칙 설정</p>
                        <p className="text-xs text-blue-500 mt-0.5">질문에 특정 키워드가 포함됐을 때, AI가 어떤 법령 조항을 우선 찾도록 할지 지정합니다.</p>
                    </div>
                    <TagInput
                        label="이 키워드가 포함된 질문에 적용 (Enter 또는 +추가)"
                        values={ruleFields.terms_any}
                        onChange={set('terms_any')}
                        placeholder="예: 밀폐공간, 산소농도"
                        color="blue"
                    />
                    <TagInput
                        label="이 대상에게만 적용 (선택 — 비워두면 모든 질문에 적용)"
                        values={ruleFields.targets_any}
                        onChange={set('targets_any')}
                        placeholder="예: 사업주, 근로자"
                        color="blue"
                    />
                    <TagInput
                        label="우선적으로 찾아볼 법령 조항 번호 *"
                        values={ruleFields.must_search_articles}
                        onChange={set('must_search_articles')}
                        placeholder="예: 제618조, 제619조"
                        color="blue"
                    />
                </div>
            );

        case 'FORBIDDEN':
            return (
                <div className="space-y-3 p-4 rounded-xl bg-red-50 border border-red-100">
                    <div>
                        <p className="text-sm font-semibold text-red-600 uppercase tracking-wider">금지 규칙 설정</p>
                        <p className="text-xs text-red-500 mt-0.5">AI 답변에 절대 나와서는 안 되는 표현을 등록합니다. 해당 표현이 포함되면 자동으로 FAIL 처리됩니다.</p>
                    </div>
                    <TagInput
                        label="답변에 나오면 안 되는 표현 * (Enter 또는 +추가)"
                        values={ruleFields.banned_phrases}
                        onChange={set('banned_phrases')}
                        placeholder="예: 적용되지 않습니다, 해당 없습니다"
                        color="red"
                    />
                </div>
            );

        case 'ANSWER_FORMAT':
            return (
                <div className="space-y-3 p-4 rounded-xl bg-purple-50 border border-purple-100">
                    <div>
                        <p className="text-sm font-semibold text-purple-600 uppercase tracking-wider">답변 구성 규칙 설정</p>
                        <p className="text-xs text-purple-500 mt-0.5">AI 답변에 반드시 포함되어야 할 항목을 지정합니다. 빠진 항목이 있으면 감점됩니다.</p>
                    </div>
                    <TagInput
                        label="이 대상에게만 적용 (선택 — 비워두면 모든 질문에 적용)"
                        values={ruleFields.targets_any}
                        onChange={set('targets_any')}
                        placeholder="예: 사업주, 근로자"
                        color="purple"
                    />
                    <TagInput
                        label="답변에 반드시 들어가야 할 항목 *"
                        values={ruleFields.must_include_sections}
                        onChange={set('must_include_sections')}
                        placeholder="예: 근거법령, 처벌내용, 의무 주체"
                        color="purple"
                    />
                </div>
            );

        case 'INTERPRETATION':
            return (
                <div className="space-y-3 p-4 rounded-xl bg-amber-50 border border-amber-100">
                    <div>
                        <p className="text-sm font-semibold text-amber-600 uppercase tracking-wider">질문 해석 규칙 설정</p>
                        <p className="text-xs text-amber-600 mt-0.5">특정 표현이 포함된 질문을 AI가 더 정확하게 해석할 수 있도록 단서를 제공합니다.</p>
                    </div>
                    <TagInput
                        label="이 표현이 포함된 질문에 해석 규칙 적용 * (Enter 또는 +추가)"
                        values={ruleFields.terms_any}
                        onChange={set('terms_any')}
                        placeholder="예: 적용 여부, 해당되나요, 대상인지"
                        color="amber"
                    />
                    <TagInput
                        label="이 대상에게만 적용 (선택)"
                        values={ruleFields.targets_any}
                        onChange={set('targets_any')}
                        placeholder="예: 사업주"
                        color="amber"
                    />
                </div>
            );

        case 'RERANK':
            return (
                <div className="space-y-3 p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                    <div>
                        <p className="text-sm font-semibold text-emerald-600 uppercase tracking-wider">재랭킹 규칙 설정</p>
                        <p className="text-xs text-emerald-600 mt-0.5">검색 결과 중 특정 조항이 더 위에 오도록 우선순위를 조정합니다.</p>
                    </div>
                    <TagInput
                        label="이 키워드가 포함된 질문에 적용 (Enter 또는 +추가)"
                        values={ruleFields.terms_any}
                        onChange={set('terms_any')}
                        placeholder="예: 소음, 진동, 화학물질"
                        color="emerald"
                    />
                    <TagInput
                        label="우선 순위를 올릴 법령 조항 번호 *"
                        values={ruleFields.must_search_articles}
                        onChange={set('must_search_articles')}
                        placeholder="예: 제618조"
                        color="emerald"
                    />
                </div>
            );

        default:
            return null;
    }
};

export default function PolicyTab() {
    const { auth } = useContext(AuthContext);
    const [params, setParams] = useState({ page: 1, size: PER });
    const [filterType, setFilterType] = useState('');
    const [filterActive, setFilterActive] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [editPolicy, setEditPolicy] = useState(null);
    const [ruleFields, setRuleFields] = useState({ ...EMPTY_RULE_FIELDS });
    const [advancedMode, setAdvancedMode] = useState(false);
    const [rawJson, setRawJson] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [expandedId, setExpandedId] = useState(null);

    const location = useLocation();

    useEffect(() => {
        if (location.state?.prefill) {
            const prefill = { ...EMPTY_FORM, ...location.state.prefill };
            setEditPolicy(prefill);
            setRuleFields({ ...EMPTY_RULE_FIELDS });
            setAdvancedMode(false);
            setRawJson('');
            setIsEdit(false);
            setShowForm(true);
            window.history.replaceState({}, '');
        }
    }, []);

    const queryParams = {
        ...params,
        ...(filterType ? { policy_type: filterType } : {}),
        ...(filterActive ? { is_active: filterActive } : {}),
    };
    const { data, isLoading } = usePolicies(queryParams);
    const policies = data?.items ?? [];
    const total = data?.total ?? 0;
    const totalPages = Math.ceil(total / PER);

    const { data: detailData } = usePolicyDetail(editingId);
    const { data: expandedData } = usePolicyDetail(expandedId);
    const { create, update, remove, activate, deactivate } = usePolicyMutations();

    const openNew = () => {
        const ts = Date.now().toString().slice(-6);
        setEditingId(null);
        setEditPolicy({ ...EMPTY_FORM, policy_code: `POL_${ts}` });
        setRuleFields({ ...EMPTY_RULE_FIELDS });
        setAdvancedMode(false);
        setRawJson('');
        setIsEdit(false);
        setShowForm(true);
    };

    const openEdit = (pol) => {
        setEditingId(pol.id);
        setIsEdit(true);
        setShowForm(true);
    };

    // 수정 시 detailData 로드 → 폼 반영
    useEffect(() => {
        if (detailData && isEdit) {
            setEditPolicy({
                ...detailData,
                tags_json: (detailData.tags_json ?? []).join(', '),
            });
            const fields = parsePayloadToFields(detailData.rule_payload_json);
            setRuleFields(fields);
            setRawJson(JSON.stringify(
                typeof detailData.rule_payload_json === 'object'
                    ? detailData.rule_payload_json
                    : JSON.parse(detailData.rule_payload_json || '{}'),
                null, 2
            ));
            setAdvancedMode(false);
        }
    }, [detailData]);

    // policy_type 변경 시 ruleFields 초기화
    const handleTypeChange = (newType) => {
        setEditPolicy((prev) => ({ ...prev, policy_type: newType }));
        setRuleFields({ ...EMPTY_RULE_FIELDS });
        setRawJson('');
    };

    // 일반 ↔ JSON 직접 편집 모드 전환
    const toggleAdvancedMode = () => {
        if (!advancedMode) {
            // 일반 → 고급: 현재 필드를 JSON으로 변환
            const payload = buildPayload(editPolicy.policy_type, ruleFields);
            setRawJson(JSON.stringify(payload, null, 2));
        } else {
            // 고급 → 일반: JSON을 필드로 파싱
            try {
                const parsed = JSON.parse(rawJson);
                setRuleFields(parsePayloadToFields(parsed));
            } catch {
                // 파싱 실패 시 그냥 모드만 전환 (필드는 유지)
            }
        }
        setAdvancedMode((prev) => !prev);
    };

    const handleSave = () => {
        if (!editPolicy.policy_code || !editPolicy.title) return;

        let parsedPayload;
        if (advancedMode) {
            try {
                parsedPayload = JSON.parse(rawJson);
            } catch {
                toast.warn('규칙 페이로드(JSON)가 올바른 형식이 아닙니다.');
                return;
            }
        } else {
            parsedPayload = buildPayload(editPolicy.policy_type, ruleFields);
        }

        const payload = {
            ...editPolicy,
            rule_payload_json: parsedPayload,
            tags_json: editPolicy.tags_json
                ? editPolicy.tags_json.split(',').map((s) => s.trim()).filter(Boolean)
                : [],
            priority_no: Number(editPolicy.priority_no),
        };

        if (isEdit) {
            update.mutate({ id: editPolicy.id, data: payload }, { onSuccess: () => setShowForm(false) });
        } else {
            create.mutate(payload, { onSuccess: () => setShowForm(false) });
        }
    };

    const handleToggle = (pol) => {
        if (pol.is_active === 'Y') {
            deactivate.mutate(pol.id);
        } else {
            activate.mutate(pol.id);
        }
    };

    const isSaving = create.isLoading || update.isLoading;

    const formBody = editPolicy && (
        <>
            {/* 기본 정보 */}
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">
                        정책 코드 {!isEdit && <span className="text-xs text-blue-500 normal-case font-normal ml-1">(자동 생성)</span>}
                    </label>
                    <input
                        value={editPolicy.policy_code ?? ''}
                        onChange={(e) => setEditPolicy({ ...editPolicy, policy_code: e.target.value })}
                        readOnly={!isEdit}
                        placeholder="예: POL_001"
                        className={`w-full px-3 py-2 rounded-lg text-base border font-mono outline-none focus:border-sb-primary ${isEdit ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 text-gray-500'}`}
                    />
                </div>
                <div>
                    <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">정책 유형</label>
                    <select
                        value={editPolicy.policy_type}
                        onChange={(e) => handleTypeChange(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                    >
                        {Object.entries(POLICY_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                    {POLICY_TYPE_DESC[editPolicy.policy_type] && (
                        <p className="text-sm text-gray-400 mt-1">{POLICY_TYPE_DESC[editPolicy.policy_type]}</p>
                    )}
                </div>
            </div>

            <div>
                <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">정책 제목 *</label>
                <input
                    value={editPolicy.title ?? ''}
                    onChange={(e) => setEditPolicy({ ...editPolicy, title: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                    placeholder="정책 제목"
                />
            </div>

            <div>
                <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">설명</label>
                <textarea
                    value={editPolicy.description ?? ''}
                    onChange={(e) => setEditPolicy({ ...editPolicy, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary resize-y"
                    placeholder="정책 규칙을 구체적으로 기술"
                />
            </div>

            {/* 규칙 설정 영역 */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <label className="text-sm uppercase tracking-wider text-gray-400">규칙 설정 *</label>
                    <button
                        type="button"
                        onClick={toggleAdvancedMode}
                        className="flex items-center gap-1 text-sm px-2 py-1 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
                    >
                        <Code size={11} />
                        {advancedMode ? '일반 입력으로 전환' : 'JSON 직접 편집'}
                    </button>
                </div>

                {advancedMode ? (
                    <div>
                        <textarea
                            value={rawJson}
                            onChange={(e) => setRawJson(e.target.value)}
                            rows={8}
                            className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-gray-900 text-emerald-400 outline-none focus:border-sb-primary resize-y font-mono"
                            placeholder={'{\n  "rule_kind": "search_boost",\n  "conditions": {},\n  "actions": {}\n}'}
                        />
                        <p className="text-sm text-gray-400 mt-1">JSON 문법에 맞게 직접 입력합니다.</p>
                    </div>
                ) : (
                    <RuleFieldsForm
                        policyType={editPolicy.policy_type}
                        ruleFields={ruleFields}
                        onChange={setRuleFields}
                    />
                )}
            </div>

            {/* 범위 / 우선순위 / 활성 / 태그 */}
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">적용 범위</label>
                    <select
                        value={editPolicy.target_scope}
                        onChange={(e) => setEditPolicy({ ...editPolicy, target_scope: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                    >
                        <option value="GLOBAL">전체 (모든 질문에 적용)</option>
                        <option value="QUESTION_TYPE">카테고리별 (특정 질문 유형에만 적용)</option>
                        <option value="TARGET">대상별 (특정 대상에만 적용)</option>
                    </select>
                </div>
                <div>
                    <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">적용 값</label>
                    <input
                        value={editPolicy.target_value ?? ''}
                        onChange={(e) => setEditPolicy({ ...editPolicy, target_value: e.target.value })}
                        placeholder="예: standard, penalty"
                        className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                    />
                </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
                <div>
                    <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">우선순위</label>
                    <input
                        type="number" min={1}
                        value={editPolicy.priority_no}
                        onChange={(e) => setEditPolicy({ ...editPolicy, priority_no: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                    />
                </div>
                <div>
                    <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">활성 여부</label>
                    <select
                        value={editPolicy.is_active}
                        onChange={(e) => setEditPolicy({ ...editPolicy, is_active: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                    >
                        <option value="Y">활성</option>
                        <option value="N">비활성</option>
                    </select>
                </div>
                <div>
                    <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">태그 (쉼표 구분)</label>
                    <input
                        value={editPolicy.tags_json ?? ''}
                        onChange={(e) => setEditPolicy({ ...editPolicy, tags_json: e.target.value })}
                        placeholder="태그1, 태그2"
                        className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                    />
                </div>
            </div>
        </>
    );

    return (
        <div className="space-y-4 w-full">
            {/* 헤더 */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                    <p className="text-lg font-bold text-sb-header">AI 답변 규칙 관리</p>
                    <p className="text-base text-gray-400">AI가 답변할 때 따르는 규칙을 관리합니다. 규칙을 저장하면 즉시 적용됩니다.</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={openNew} className="flex items-center gap-1.5 px-3 py-2 bg-sb-primary hover:bg-blue-700 text-white text-base font-medium rounded-lg transition-colors shrink-0">
                        <Plus size={14} /> 규칙 추가
                    </button>
                </div>
            </div>

            {/* 필터 */}
            <div className="flex flex-wrap gap-2">
                <select
                    value={filterType}
                    onChange={(e) => { setFilterType(e.target.value); setParams((p) => ({ ...p, page: 1 })); }}
                    className="px-3 py-1.5 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                >
                    <option value="">전체 유형</option>
                    {Object.entries(POLICY_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <select
                    value={filterActive}
                    onChange={(e) => { setFilterActive(e.target.value); setParams((p) => ({ ...p, page: 1 })); }}
                    className="px-3 py-1.5 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                >
                    <option value="">전체 상태</option>
                    <option value="Y">활성</option>
                    <option value="N">비활성</option>
                </select>
                <span className="text-xs text-gray-500 flex items-center px-1">총 {total}건</span>
            </div>

            {/* 목록 */}
            <div className="space-y-2">
                {isLoading && <p className="text-center py-8 text-base text-gray-400">불러오는 중...</p>}
                {!isLoading && policies.map((pol) => {
                    const pt = POLICY_TYPES[pol.policy_type] ?? POLICY_TYPES.SEARCH_RULE;
                    const isExpanded = expandedId === pol.id;
                    const detail = isExpanded ? expandedData : null;
                    const payload = detail
                        ? (typeof detail.rule_payload_json === 'object'
                            ? JSON.stringify(detail.rule_payload_json, null, 2)
                            : (detail.rule_payload_json ?? ''))
                        : null;

                    return (
                        <div key={pol.id} className={`rounded-xl border bg-white shadow-sm transition-all ${isExpanded ? 'border-sb-primary' : 'border-gray-100 hover:shadow-md'}`}>
                            <div
                                onClick={() => setExpandedId(isExpanded ? null : pol.id)}
                                className="w-full text-left p-4 cursor-pointer"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                            <span className={`text-sm px-1.5 py-0.5 rounded ${pt.bg} ${pt.color} font-medium`}>{pt.label}</span>
                                            <span className={`text-sm px-1.5 py-0.5 rounded-full font-medium ${pol.is_active === 'Y' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-500'}`}>
                                                {pol.is_active === 'Y' ? '활성' : '비활성'}
                                            </span>
                                            <span className="text-sm font-mono text-gray-400">{pol.policy_code}</span>
                                            <span className="text-sm text-gray-400">우선순위 {pol.priority_no}</span>
                                        </div>
                                        <p className="text-base font-semibold text-sb-header">{pol.title}</p>
                                        {pol.description && <p className="text-base text-gray-500 mt-0.5">{pol.description}</p>}
                                    </div>
                                    <div className="flex items-center gap-2 ml-3 shrink-0" onClick={(e) => e.stopPropagation()}>
                                        <button
                                            onClick={() => handleToggle(pol)}
                                            disabled={activate.isLoading || deactivate.isLoading}
                                            className={`text-sm px-2 py-1 rounded-lg transition-colors ${pol.is_active === 'Y' ? 'bg-gray-100 text-gray-500 hover:bg-gray-200' : 'bg-sb-primary text-white hover:bg-blue-700'}`}
                                        >
                                            {pol.is_active === 'Y' ? '비활성화' : '활성화'}
                                        </button>
                                        <ChevronDown size={15} className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                    </div>
                                </div>
                            </div>

                            {isExpanded && (
                                <div className="px-4 pb-4 border-t border-gray-100">
                                    {!detail ? (
                                        <p className="text-base text-gray-400 text-center py-4">불러오는 중...</p>
                                    ) : (
                                        <div className="pt-3 space-y-3">
                                            <div className="grid grid-cols-3 gap-2">
                                                <div className="p-2.5 rounded-lg bg-gray-50">
                                                    <p className="text-sm uppercase tracking-wider text-gray-400 mb-0.5">적용 범위</p>
                                                    <p className="text-base font-medium text-gray-700">{detail.target_scope}</p>
                                                </div>
                                                <div className="p-2.5 rounded-lg bg-gray-50">
                                                    <p className="text-sm uppercase tracking-wider text-gray-400 mb-0.5">적용 값</p>
                                                    <p className="text-base font-medium text-gray-700">{detail.target_value || '—'}</p>
                                                </div>
                                                <div className="p-2.5 rounded-lg bg-gray-50">
                                                    <p className="text-sm uppercase tracking-wider text-gray-400 mb-0.5">태그</p>
                                                    <div className="flex flex-wrap gap-1 mt-0.5">
                                                        {(detail.tags_json ?? []).length > 0
                                                            ? (detail.tags_json ?? []).map((tag, i) => (
                                                                <span key={i} className="text-sm px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">#{tag}</span>
                                                            ))
                                                            : <span className="text-base text-gray-400">—</span>
                                                        }
                                                    </div>
                                                </div>
                                            </div>

                                            <div>
                                                <p className="text-sm uppercase tracking-wider text-gray-400 mb-1">규칙 내용</p>
                                                {(() => {
                                                    const fields = parsePayloadToFields(detail.rule_payload_json);
                                                    const items = [
                                                        fields.terms_any.length       && { label: '적용 키워드',     values: fields.terms_any, color: 'bg-blue-100 text-blue-700' },
                                                        fields.targets_any.length     && { label: '적용 대상',       values: fields.targets_any, color: 'bg-gray-100 text-gray-600' },
                                                        fields.must_search_articles.length && { label: '우선 검색 조항', values: fields.must_search_articles, color: 'bg-emerald-100 text-emerald-700' },
                                                        fields.banned_phrases.length  && { label: '금지 표현',       values: fields.banned_phrases, color: 'bg-red-100 text-red-700' },
                                                        fields.must_include_sections.length && { label: '필수 포함 항목', values: fields.must_include_sections, color: 'bg-purple-100 text-purple-700' },
                                                    ].filter(Boolean);
                                                    if (!items.length) return <p className="text-sm text-gray-400">설정된 규칙 내용이 없습니다.</p>;
                                                    return (
                                                        <div className="space-y-2">
                                                            {items.map((item, i) => (
                                                                <div key={i} className="flex items-start gap-2">
                                                                    <span className="text-xs text-gray-400 w-24 shrink-0 pt-0.5">{item.label}</span>
                                                                    <div className="flex flex-wrap gap-1">
                                                                        {item.values.map((v, j) => (
                                                                            <span key={j} className={`text-xs px-2 py-0.5 rounded-full font-mono ${item.color}`}>{v}</span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    );
                                                })()}
                                            </div>

                                            <div className="flex items-center justify-end gap-2 pt-1">
                                                <button
                                                    onClick={() => {
                                                        if (window.confirm(`"${pol.title}" 정책을 삭제하시겠습니까?`)) {
                                                            remove.mutate(pol.id);
                                                        }
                                                    }}
                                                    disabled={remove.isLoading}
                                                    className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 transition-colors"
                                                >
                                                    삭제
                                                </button>
                                                <button
                                                    onClick={() => openEdit(pol)}
                                                    className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-sb-primary text-white hover:bg-blue-700 transition-colors"
                                                >
                                                    <Edit3 size={11} /> 수정
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
                {!isLoading && policies.length === 0 && (
                    <div className="text-center py-12 space-y-2">
                        <p className="text-base font-medium text-gray-500">아직 등록된 규칙이 없습니다.</p>
                        <p className="text-sm text-gray-400">결과 검토 탭에서 문제점을 발견했다면, <strong>규칙 추가</strong> 버튼으로 개선 규칙을 추가하세요.</p>
                        <p className="text-sm text-gray-400">예: AI가 특정 표현을 잘못 답변한다면 <em>금지 규칙</em>으로 차단할 수 있습니다.</p>
                    </div>
                )}
            </div>
            <Pagination currentPage={params.page} totalPages={totalPages} onPageChange={(p) => setParams((prev) => ({ ...prev, page: p }))} />

            {/* 규칙 추가/수정 모달 */}
            <Modal show={showForm} centered size="lg" onHide={() => setShowForm(false)} contentClassName="rounded-2xl border-0 shadow-xl">
                <Modal.Header className="border-b border-gray-100 px-6 py-3">
                    <Modal.Title className="text-base font-bold text-sb-header">
                        {isEdit ? '규칙 수정' : '규칙 추가'}
                    </Modal.Title>
                    <button type="button" className="modal-btn-close" onClick={() => setShowForm(false)}>
                        <span className="modal-btn-span">X</span>
                    </button>
                </Modal.Header>
                <Modal.Body className="max-h-[65vh] overflow-y-auto px-6 py-4 space-y-3">
                    {formBody}
                </Modal.Body>
                <Modal.Footer className="border-0 px-6 pb-4 pt-0">
                    <Button variant="light" className="text-gray-600" onClick={() => setShowForm(false)}>취소</Button>
                    <Button variant="primary" className="bg-sb-primary border-0" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? '저장 중...' : '저장'}
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );
}
